import { call, put, select } from 'redux-saga/effects';
import { PRODUCTS_COMPARE_MANAGE } from '../../actions';
import { getUnifiedCompareData } from '../../selectors/base';
import { UnifiedCompareState, ProductErrorState, ApiError } from '../../types';
import { putList } from '../../api';
import { transformCompareListsToAPI } from '../../utils';
import { ApiCancelToken } from 'api';
import { updateCompareState, isDataSyncError, handleBadRequestError } from './utils';

const ERROR_MESSAGES = {
  UPDATE_LIST_ERROR: 'Ошибка обновления списка',
} as const;

/**
 * NOTE: Сага для обновления существующего списка сравнения
 * Обновляет список на сервере и синхронизирует локальное состояние
 */
export function* updateListSaga(params: {
  customerId: string;
  host: string;
  abortController: { token: ApiCancelToken };
  currentLists: ReturnType<typeof transformCompareListsToAPI>;
  productId: string;
  list: { name: string; groupId: string; products: string[] };
}): Generator {
  const currentState: UnifiedCompareState = yield select(getUnifiedCompareData);

  try {
    const { customerId, host, abortController, list } = params;

    const body = {
      customerId,
      compareList: {
        name: list.name,
        groupId: list.groupId,
        products: list.products,
      },
    };

    const existingGroup = currentState[list.groupId];

    if (existingGroup) {
      const newProductsData: Record<string, { productId: string }> = {};

      list.products.forEach((prodId) => {
        newProductsData[prodId] = existingGroup.productsData[prodId] || { productId: prodId };
      });

      const updatedState = updateCompareState(currentState, list.groupId, {
        name: list.name,
        productsData: newProductsData,
        isDataLoaded: existingGroup.isDataLoaded,
        isSyncedWithServer: true,
      });

      yield put(PRODUCTS_COMPARE_MANAGE.success(updatedState));
    }

    yield call(putList, body, host, abortController.token);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(ERROR_MESSAGES.UPDATE_LIST_ERROR);
    const apiError = error as ApiError;

    if (isDataSyncError(apiError)) {
      // eslint-disable-next-line no-console
      console.warn('400/409 ошибка при обновлении списка - инициирована синхронизация данных', {
        groupId: params.list.groupId,
        productId: params.productId,
        errorStatus: apiError?.response?.status || apiError?.status || 'unknown',
        timestamp: new Date().toISOString(),
      });
      yield* handleBadRequestError();
    }

    const errorState: ProductErrorState = {
      error: err,
      productId: params.productId,
      groupId: params.list.groupId,
      currentState,
    };

    yield put(PRODUCTS_COMPARE_MANAGE.failure(errorState));
  }
}
